import type { TicketAuditEntry } from '@nbh/shared';
import { propertyDateKey } from '@nbh/shared';
import { propertyTimeZone } from '../../config/property';

/**
 * The shape both threads render — a ticket message and a direct message, reduced to what
 * a bubble needs. Ticket messages carry the two optional fields; DMs never do.
 */
export interface ThreadMessage {
  id: string;
  body: string;
  /** Null once the author's account is gone (D25); still somebody else's bubble. */
  senderId: string | null;
  senderName: string;
  createdAt: string;
  isInternal?: boolean;
  attachments?: ThreadAttachment[];
}

export interface ThreadAttachment {
  id: string;
  url: string;
  mimeType: string;
}

/**
 * Consecutive bubbles from one sender within this window collapse into a run: the name
 * once at the top, the tail on the first only, tighter spacing between. The reference
 * does exactly this, and it is what keeps a quick back-and-forth from reading as a
 * column of identical headers.
 */
export const RUN_WINDOW_MS = 3 * 60_000;

export type ThreadItem =
  /** A centered day pill. `at` is the first instant of that day in the thread. */
  | { kind: 'day'; dayKey: string; at: string }
  | { kind: 'message'; message: ThreadMessage; firstOfRun: boolean }
  /** A lifecycle event, inline (D5). Ticket threads only; DMs pass none. */
  | { kind: 'event'; entry: TicketAuditEntry };

/**
 * The thread, as rendered: messages and lifecycle events merged by time, day separators
 * where the property day changes, and runs marked.
 *
 * Pure, so the grouping rules are testable without a screen. Days are property days,
 * never the device's — the same rule as every timestamp in the app.
 *
 * An event ends a run. A bubble after "Ana put the job on hold" starts fresh even if the
 * same person sent the one before it, because the thing between them was not theirs.
 */
export function buildThread(
  messages: readonly ThreadMessage[],
  events: readonly TicketAuditEntry[] = [],
  timeZone: string = propertyTimeZone,
): ThreadItem[] {
  const merged: Array<
    { kind: 'message'; message: ThreadMessage; at: number } | { kind: 'event'; entry: TicketAuditEntry; at: number }
  > = [
    ...messages.map((message) => ({
      kind: 'message' as const,
      message,
      at: Date.parse(message.createdAt),
    })),
    ...events.map((entry) => ({ kind: 'event' as const, entry, at: Date.parse(entry.createdAt) })),
  ].sort((a, b) => a.at - b.at);

  const items: ThreadItem[] = [];
  let dayKey: string | null = null;
  let previous: { senderId: string | null; at: number } | null = null;

  for (const entry of merged) {
    const createdAt = entry.kind === 'message' ? entry.message.createdAt : entry.entry.createdAt;
    const key = propertyDateKey(new Date(entry.at), timeZone);
    if (key !== dayKey) {
      items.push({ kind: 'day', dayKey: key, at: createdAt });
      dayKey = key;
      previous = null;
    }

    if (entry.kind === 'event') {
      items.push({ kind: 'event', entry: entry.entry });
      previous = null;
      continue;
    }

    const { message } = entry;
    const continues =
      previous !== null &&
      previous.senderId !== null &&
      previous.senderId === message.senderId &&
      entry.at - previous.at <= RUN_WINDOW_MS;
    items.push({ kind: 'message', message, firstOfRun: !continues });
    previous = { senderId: message.senderId, at: entry.at };
  }

  return items;
}

/** What a media attachment is, for the glyph beside a preview. */
export function mediaKind(mimeType: string): 'photo' | 'voice' | 'file' {
  if (mimeType.startsWith('image/')) return 'photo';
  if (mimeType.startsWith('audio/')) return 'voice';
  return 'file';
}

/** "Ana" from "Ana Ferreira" — the prefix a group preview carries. */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/**
 * Initials for the avatar disc: first letters of the first and last words, so "Ana
 * Ferreira" is AF and a single name is its first letter.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0]!.charAt(0);
  const last = words.length > 1 ? words[words.length - 1]!.charAt(0) : '';
  return (first + last).toLocaleUpperCase();
}

/**
 * A stable hue per user id, so a person's disc is the same colour everywhere.
 *
 * Eight hues, all chosen to carry near-white initials at 4.5:1 or better on a dark
 * screen. A hash of the id rather than the name: names collide, ids do not, and a
 * renamed account keeps its colour.
 */
export const AVATAR_HUES = [
  'bg-emerald-700',
  'bg-sky-700',
  'bg-violet-700',
  'bg-rose-700',
  'bg-amber-700',
  'bg-teal-700',
  'bg-indigo-700',
  'bg-fuchsia-700',
] as const;

export function avatarHue(id: string): (typeof AVATAR_HUES)[number] {
  let hash = 0;
  for (let index = 0; index < id.length; index += 1) {
    hash = (hash * 31 + id.charCodeAt(index)) >>> 0;
  }
  return AVATAR_HUES[hash % AVATAR_HUES.length]!;
}
